import { backendClient } from "@/api/backendClient";
import {
  type DeviceDetailCorrected,
  type ListDevicesProps,
  type MoveDeviceProps,
  type SearchDevicesProps,
} from "@/api/resources/devices/types";
import {
  type ApiResourceConfig,
  type ApiViewResourceConfig,
  createGlobalFilterArgs,
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  createSearchRpcSupabaseQuery,
  spawnGetRpcApiFunction,
  spawnListRpcInApiFunction,
} from "@/utils/api";

import type { Device, DeviceInsert, DeviceUpdate } from "@waku-care/types/contracts/internal";

const table = "devices";
export const query = `
                      *,
                      device_tags(*),
                      manufacturer:manufacturers(*),
                      model:device_models(*)
                      `;
export const apiConfigDevices: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "ref", "software_version"],
  sortables: ["name", "ref", "created_at", "updated_at", "last_auth", "manufacturer.name", "model.name"],
};

export const queryCharts = `
                            *,
                            device_tags(*),
                            manufacturer:manufacturers(*),
                            model:device_models(*),
                            device_insights_charts(id)
                            `;
export const apiConfigDevicesCharts: ApiResourceConfig<typeof table, typeof queryCharts> = {
  table,
  query: queryCharts,
  searchables: ["name", "ref", "software_version"],
  sortables: ["name", "ref", "created_at", "updated_at", "last_auth", "manufacturer.name", "model.name"],
};

const view = "device_details";
export const queryView = "*";
export const apiConfigDeviceDetailsView: ApiViewResourceConfig<typeof view, typeof queryView> = {
  view,
  query: queryView,
  searchables: ["name", "ref", "software_version"],
  sortables: ["name", "ref", "created_at", "updated_at", "last_auth", "manufacturer_name", "model_name"],
};

export const getDevice = spawnGetRpcApiFunction({ rpc: "get_device_detail" });
export const getDevicesByIds = spawnListRpcInApiFunction({
  rpc: "list_device_details",
});
// devices are written by the backend, eg. a new or moved device gets the triggers of its model's model triggers
export const createDevice = async ({ values }: { values: DeviceInsert }) => {
  const { data, error } = await backendClient.post<Device>(`/devices`, { body: values });
  if (error) throw error;
  return data;
};

export const updateDevice = async ({ match, values }: { match: { id: string }; values: DeviceUpdate }) => {
  const { data, error } = await backendClient.patch<Device>(`/devices/${match.id}`, { body: values });
  if (error) throw error;
  return data;
};

export const deleteDevice = async (id: string) => {
  const { error } = await backendClient.delete<void>(`/devices/${id}`);
  if (error) throw error;
};

export const moveDevice = async ({ deployment_id, id }: MoveDeviceProps) => {
  const { error } = await backendClient.post<Device>(`/devices/${id}/move`, { body: { deployment_id } });
  if (error) throw error;
};

export async function getDevicesBySearch({
  searchOptions,
  customFilters,
  customOrFilters,
  globalFilters,
  limit,
}: SearchDevicesProps) {
  const _filters = createGlobalFilterArgs(globalFilters, searchOptions.search);

  const { data, count, error } = await createSearchRpcSupabaseQuery({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "list_device_details",
        {
          _filters,
        },
        { count: "estimated" },
      ),
      sortables: apiConfigDeviceDetailsView.sortables,
      filters: [
        ["deleted_at", "is", null], // filter out soft-deleted devices
      ],
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });
  if (error) throw error;
  return { data: data as DeviceDetailCorrected[], count };
}

export async function getPaginatedDevicesFromRpc({
  dataTableOptions,
  customFilters,
  customOrFilters,
  globalFilters,
}: ListDevicesProps) {
  const _filters = createGlobalFilterArgs(globalFilters, dataTableOptions.search);

  const { data, count, error } = await createPaginatedRpcSupabaseQuery({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "list_device_details",
        {
          _filters,
        },
        { count: "estimated" },
      ),
      sortables: apiConfigDeviceDetailsView.sortables,
      filters: [
        ["deleted_at", "is", null], // filter out soft-deleted devices
      ],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });

  if (error) throw error;
  return { data: data as DeviceDetailCorrected[], count };
}
